import type { Empresa } from "@/componentes/empresa/tipos"
import type { Persona } from "@/componentes/personas/persona"

import { filasDeLaCara } from "./contenido"
import { Filas } from "./Filas"
import { FondoCarnet } from "./FondoCarnet"
import { CARNET, MARGEN, PIE, RETRATO } from "./medidas"
import { PieCarnet } from "./PieCarnet"
import { cuerpoEnUnaLinea } from "./tipografia"

/**
 * La cara del carnet: retrato, nombre, cargo, datos y, al pie, la marca y el QR.
 *
 * ⚠ **No trae ni un dato propio: todo sale de la ficha de la persona y de la
 * configuración de la empresa.** Ésa es la decisión que define el módulo. Un carnet
 * con sus propios campos sería un tercer sitio donde vive el cargo de alguien —los
 * otros dos son `personas` y el organigrama— y el día que alguien ascienda habría que
 * acordarse de este. Aquí se corrige la ficha y el siguiente carnet sale corregido.
 *
 * ⚠ **El componente es PURO: recibe la foto ya resuelta.** El retrato viaja como
 * `blob` desde un endpoint autenticado (ver `useFotoPersona`), y pedirlo aquí dentro
 * significaría que el pliego —que pinta esta misma cara veinte veces— montara veinte
 * consultas. Lo pide la pantalla una vez y lo pasa.
 *
 * ⚠ **Sin foto se dibuja el círculo vacío y NO un texto que diga «sin foto».** Lo que
 * se ve en pantalla es lo que sale de la impresora (es la razón de ser de estos
 * módulos), así que un aviso pintado dentro de la pieza acabaría impreso en una
 * tarjeta plastificada. Quien avisa es la pantalla, y además impide imprimir.
 *
 * ⚠ **El nombre va en DOS líneas fijas: nombres arriba, apellidos abajo.** Es como se
 * lee un documento de identidad, y además resuelve el ancho: cada línea encoge hasta
 * caber entera y las dos van al mismo cuerpo. Envolverlo donde cayera daba
 * «MARÍA / ALEJANDRA PÉREZ / ROJAS», con los apellidos separados. Partirlo por letras
 * es lo que prohíbe `.claude/rules/frontend.md`.
 *
 * ⚠ **Arriba no va el logotipo: la foto sube a ocupar su sitio.** La marca está en el
 * pie (`PieCarnet`), y el nombre y el cargo van como texto sobre el papel: el color se
 * queda en las olas.
 */
export function CarnetFrente({
  persona,
  empresa,
  foto,
}: {
  persona: Persona
  /** Para el pie y las filas. Se pasa resuelta: el pliego pinta muchas copias. */
  empresa?: Empresa
  /** La URL de objeto del retrato, o `null` si la ficha no tiene foto. */
  foto: string | null
}) {
  const { ancho, alto } = CARNET
  const util = ancho - MARGEN * 2
  const nombre = `${persona.nombres} ${persona.apellidos}`
  const enLinea = (texto: string) =>
    cuerpoEnUnaLinea(texto, util, { tope: 3.9, minimo: 2.6, espaciado: 0.1 })
  const cuerpoNombre = Math.min(enLinea(persona.nombres), enLinea(persona.apellidos))

  return (
    <article
      className="pieza-impresa"
      style={{
        position: "relative",
        width: `${ancho}mm`,
        height: `${alto}mm`,
        fontFamily: "var(--font-carnet)",
      }}
      aria-label={`Carnet de ${nombre}`}
    >
      <FondoCarnet cara="frente" />

      {/* El relleno de abajo es el pie: lo que no quepa encima lo cede el retrato. */}
      <div
        style={{
          position: "relative",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: `7mm ${MARGEN}mm ${PIE}mm`,
          textAlign: "center",
          color: "var(--impreso-tinta)",
        }}
      >
        <Retrato foto={foto} nombre={nombre} />

        <p
          style={{
            flexShrink: 0,
            margin: "2.4mm 0 0",
            fontSize: `${cuerpoNombre}mm`,
            fontWeight: 700,
            lineHeight: 1.08,
            letterSpacing: "0.1mm",
            textTransform: "uppercase",
          }}
        >
          <span style={{ display: "block" }}>{persona.nombres}</span>{" "}
          <span style={{ display: "block" }}>{persona.apellidos}</span>
        </p>
        {persona.cargo ? (
          <p
            style={{
              flexShrink: 0,
              margin: "0.9mm 0 0",
              fontSize: `${cuerpoEnUnaLinea(persona.cargo, util, { tope: 2.5, minimo: 2, espaciado: 0.3 })}mm`,
              fontWeight: 700,
              letterSpacing: "0.3mm",
              lineHeight: 1.15,
              textTransform: "uppercase",
              color: "var(--impreso-vino)",
            }}
          >
            {persona.cargo}
          </p>
        ) : null}

        <Filas
          filas={filasDeLaCara(persona, empresa)}
          ancho={util}
          style={{ flexShrink: 0, marginTop: "2.4mm" }}
        />
      </div>

      <PieCarnet empresa={empresa} />
    </article>
  )
}

/**
 * El retrato, en su círculo con aro blanco y filete dorado.
 *
 * ⚠ **Encuadrado por encima del centro** (`object-position: 50% 30%`). En una foto de
 * teléfono en 9:16, recortar al centro deja fuera la franja de arriba —la frente— y
 * llena el círculo de pecho; la cara de un retrato está en la mitad de arriba.
 *
 * ⚠ **Encoge con el alto y el ancho lo sigue** (`aspect-ratio`). Es lo único de la cara
 * que cede cuando el texto no cabe (ver `RETRATO`); todo lo demás lleva
 * `flex-shrink: 0`, porque un texto encogido se sale de su caja en vez de hacerse
 * pequeño.
 */
function Retrato({ foto, nombre }: { foto: string | null; nombre: string }) {
  const caja = { display: "block", width: "100%", height: "100%", borderRadius: "50%" } as const

  return (
    <div
      style={{
        flex: "0 1 auto",
        height: `${RETRATO.diametro}mm`,
        minHeight: `${RETRATO.minimo}mm`,
        aspectRatio: "1",
        padding: "1.1mm",
        borderRadius: "50%",
        background: "var(--impreso-papel)",
        border: "0.35mm solid var(--impreso-oro-2)",
      }}
    >
      {foto ? (
        <img
          src={foto}
          alt={`Fotografía de ${nombre}`}
          style={{ ...caja, objectFit: "cover", objectPosition: "50% 30%" }}
        />
      ) : (
        <div style={{ ...caja, background: "var(--impreso-linea)" }} aria-hidden={true} />
      )}
    </div>
  )
}
